import React, { useCallback, useEffect, useState } from "react";
import { IconButton, NumberInput } from "@bloomberg/media.atomics";
import { RemoveCircleOutline } from "@bloomberg/media.icons";

import styles from "./styles.module.css";
import Datepicker from "~components/Datepicker";

export type HoldingRowState = {
  readonly id: string;
  readonly date: string;
  readonly shares: string;
  readonly price: string;
};

export type FieldErrors = {
  readonly date?: string;
  readonly shares?: string;
  readonly price?: string;
};

type HoldingsRowProps = {
  readonly holding: HoldingRowState;
  readonly onDateChange: (id: string, value: string) => void;
  readonly onSharesChange: (id: string, value: string) => void;
  readonly onPriceChange: (id: string, value: string) => void;
  readonly onRemove: (id: string) => void;
  readonly disableRemove?: boolean;
  readonly onValidationChange?: (hasErrors: boolean) => void;
};

function validateDate(value: string): string | undefined {
  if (!value) return "Required";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "Invalid date";

  if (d > new Date()) return "Date cannot be in the future";

  return undefined;
}

function validateNumber(value: string): string | undefined {
  if (!value) return "Required";

  if (Number.isNaN(Number(value))) return "Invalid";

  return undefined;
}

function hasAnyError(errors: FieldErrors): boolean {
  return Boolean(errors.date ?? errors.shares ?? errors.price);
}

export default function HoldingsRow({
  holding,
  onDateChange,
  onSharesChange,
  onPriceChange,
  onRemove,
  disableRemove = false,
  onValidationChange,
}: HoldingsRowProps) {
  const { id, date, shares, price } = holding;
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    onValidationChange?.(hasAnyError(fieldErrors));
  }, [fieldErrors, onValidationChange]);

  const handleDateChange = useCallback(
    (value: Date) => {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");

      onDateChange(id, `${year}-${month}-${day}`);
      setFieldErrors((prev) => ({ ...prev, date: undefined }));
    },
    [id, onDateChange]
  );

  const handleSharesChange = useCallback(
    (value: string) => {
      onSharesChange(id, value);
      setFieldErrors((prev) => ({ ...prev, shares: undefined }));
    },
    [id, onSharesChange]
  );

  const handlePriceChange = useCallback(
    (value: string) => {
      onPriceChange(id, value);
      setFieldErrors((prev) => ({ ...prev, price: undefined }));
    },
    [id, onPriceChange]
  );

  const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

  const handleDateBlur = useCallback(() => {
    const error = validateDate(date);

    setFieldErrors((prev) => ({ ...prev, date: error }));
  }, [date]);

  const handleSharesBlur = useCallback(() => {
    const error = validateNumber(shares);

    setFieldErrors((prev) => ({ ...prev, shares: error }));
  }, [shares]);

  const handlePriceBlur = useCallback(() => {
    const error = validateNumber(price);

    setFieldErrors((prev) => ({ ...prev, price: error }));
  }, [price]);

  return (
    <div className={styles.holdingsRow}>
      <div className={`${styles.holdingsCell} ${styles.holdingsDatepicker}`}>
        <div onBlur={handleDateBlur}>
          <Datepicker
            native={false}
            label=""
            dateFormat="yyyy-MM-dd"
            placeholder="YYYY-MM-DD"
            maxDate={new Date()}
            popperPlacement="top-start"
            value={date}
            onChange={handleDateChange}
            error={fieldErrors.date}
          />
        </div>
        {fieldErrors.date !== undefined && (
          <p className={styles.fieldError}>{fieldErrors.date}</p>
        )}
      </div>
      <div className={styles.holdingsCell}>
        <NumberInput
          labelText="Shares"
          size="small"
          step={1}
          value={shares}
          onChange={handleSharesChange}
          onBlur={handleSharesBlur}
          errorText={fieldErrors.shares}
        />
      </div>
      <div className={styles.holdingsCell}>
        <NumberInput
          labelText="Price"
          size="small"
          step={0.01}
          value={price}
          onChange={handlePriceChange}
          onBlur={handlePriceBlur}
          errorText={fieldErrors.price}
        />
      </div>
      <div className={styles.holdingsRemoveBtn}>
        <IconButton
          variant="ghost"
          size="small"
          label="Remove holding"
          onClick={handleRemove}
          disabled={disableRemove}
        >
          <RemoveCircleOutline />
        </IconButton>
      </div>
    </div>
  );
}
